import React, {
  memo,
  useEffect,
  useRef,
} from 'react';

import { Typography } from 'antd';
import { GoListUnordered } from 'react-icons/go';
import InfiniteScroll from 'react-infinite-scroller';

import Loader from '../../../../components/V2/Loader/Loader';
import { useTheme } from '../../../../context/ThemeProvider';
import { useAppNavigate } from '../../../../shared/hooks';
import { dynamicItemTitle } from '../../../../shared/utils';
import { ROUTES } from '../../../../types/routes';
import {
  ITQActivityLog,
  TQServiceRequestTypeValue,
} from '../../../../types/tqConnectTypes/service-requests';
import NoDataFound from '../../../Feed/components/NoDataFound/NoDataFound';
import HeaderTextWithLine
  from '../../components/HeaderTextWithLine/HeaderTextWithLine';
import RecentActivityCard from './RecentActivityCard';

interface Props {
  requestType: TQServiceRequestTypeValue;
  groupedByDateList: Record<string, ITQActivityLog[]>;
  total: number;
  loading: boolean;
  isFetchingNextPage: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onMarkAsRead: (activityId: string) => void;
}
const RecentActivityList: React.FC<Props> = ({
  requestType,
  groupedByDateList,
  total,
  loading,
  isFetchingNextPage,
  hasMore,
  onLoadMore,
  onMarkAsRead,
}) => {
  const navigate = useAppNavigate();
  const { colors: COLORS } = useTheme();

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const element = document.getElementById(`recent-activity-list`);

    if (element) {
      scrollContainerRef.current = element;
    }
  }, []);

  const renderActivityList = (list: ITQActivityLog[]) => {
    return list.map((activity) => (
      <RecentActivityCard
        key={`recent-activity-card-${activity._id}`}
        activity={activity}
        onClick={
          activity.read && !activity?.serviceRequest?._id
            ? undefined
            : () => {
                if (!activity.read) {
                  onMarkAsRead(activity._id);
                }

                if (activity?.serviceRequest?._id) {
                  const route =
                    ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DETAILS.replace(
                      ':serviceRequestId',
                      activity.serviceRequest._id,
                    );
                  navigate(route as ROUTES, {
                    search: `?from=${requestType}`,
                  });
                }
              }
        }
      />
    ));
  };

  return (
    <div className="recent-activity__list" id="recent-activity-list">
      <InfiniteScroll
        pageStart={0}
        loadMore={onLoadMore}
        hasMore={hasMore}
        useWindow={false}
        getScrollParent={() => scrollContainerRef.current}
        loader={
          (!loading && (hasMore || isFetchingNextPage) ? (
            <Loader type="spinner" color={COLORS.ICON} />
          ) : null) as JSX.Element
        }>
        {loading ? <Loader type="spinner" color={COLORS.ICON} /> : null}

        {Object.entries(groupedByDateList).map(([date, activities]) => (
          <div key={date}>
            <HeaderTextWithLine
              title={date}
              style={{
                paddingInline: 16,
                marginBottom: 12,
              }}
              // lineStyle={{
              //   backgroundColor: COLORS.BORDER,
              // }}
            />
            {renderActivityList(activities)}
          </div>
        ))}

        {total > 0 && !loading && !hasMore ? (
          <Typography.Paragraph
            style={{
              width: '100%',
              textAlign: 'center',
              marginBlock: 32,
            }}>
            That’s it! No more activities to show.
          </Typography.Paragraph>
        ) : null}
      </InfiniteScroll>

      {total === 0 && !loading && !hasMore ? (
        <NoDataFound
          iconElement={<GoListUnordered size={96} color={COLORS.TEXT1} />}
          title="No activities found yet!"
          subtitle={`Here you'll see all the updates on your ${dynamicItemTitle('tq-offering-requests', 'lower')} and help offers.`}
          style={{
            width: '100%',
            padding: 16,
            paddingTop: 48,
          }}
        />
      ) : null}
    </div>
  );
};

export default memo(RecentActivityList);
